import { useEffect, useMemo, useState } from "react";
import { Code2, Copy, Check, Puzzle } from "lucide-react";
import api from "../lib/api";

/* Partner page: generate the one-line embed snippet for the IndianAirports
   planner widget and preview it live. */
export default function IndiaPartners() {
  const [airports, setAirports] = useState([]);
  const [iata, setIata] = useState("DEL");
  const [accent, setAccent] = useState("#1d4ed8");
  const [height, setHeight] = useState(460);
  const [copied, setCopied] = useState(false);
  // Clamp for the snippet + preview so partners can't copy an invalid embed
  const h = Math.min(640, Math.max(320, parseInt(height, 10) || 460));

  useEffect(() => {
    document.title = "Embed the planner widget — IndianAirports.com";
    api.get("/india/airports").then((r) => setAirports(r.data.items || []));
    window.scrollTo(0, 0);
  }, []);

  const snippet = useMemo(() =>
    `<script src="${window.location.origin}/india-widget.js" data-airport="${iata}" data-accent="${accent}" data-height="${h}" async></script>`,
    [iata, accent, h]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(snippet); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard blocked */ }
  };

  const previewSrc = `/widgets/india-planner?embed=1&airport=${iata}&accent=${encodeURIComponent(accent)}&partner=preview`;

  return (
    <div data-testid="india-partners-page">
      <div className="bg-gradient-to-br from-blue-50 via-white to-orange-50 border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-5 py-10">
          <p className="text-xs font-800 uppercase tracking-wide text-blue-700 inline-flex items-center gap-1.5"><Puzzle className="w-3.5 h-3.5" /> For partners</p>
          <h1 className="mt-1 font-heading font-900 text-4xl sm:text-5xl tracking-tight" data-testid="india-partners-title">Embed our airport planner</h1>
          <p className="mt-2 text-base text-zinc-600 max-w-2xl">
            Give your visitors instant airport guides and trip planning — one line of code, no account needed.
            Traffic from your site is tagged with your domain automatically.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8 grid lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-heading font-800 text-lg md:text-lg flex items-center gap-2"><Code2 className="w-5 h-5 text-blue-700" /> Configure your widget</h2>
          <div className="mt-4 space-y-4 bg-white border border-zinc-200 rounded-2xl p-5">
            <label className="block">
              <span className="text-[10px] font-800 uppercase text-zinc-400">Default airport</span>
              <select value={iata} onChange={(e) => setIata(e.target.value)} data-testid="india-partners-airport"
                className="mt-1 w-full text-sm border border-zinc-300 rounded-xl px-2.5 py-2 bg-white focus:outline-none focus:border-blue-400">
                {airports.map((a) => <option key={a.iata} value={a.iata}>{a.city} — {a.iata}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[10px] font-800 uppercase text-zinc-400">Accent color</span>
                <span className="mt-1 flex items-center gap-2">
                  <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} data-testid="india-partners-accent" className="w-10 h-9 rounded cursor-pointer border border-zinc-300" />
                  <span className="text-xs font-mono text-zinc-500">{accent}</span>
                </span>
              </label>
              <label className="block">
                <span className="text-[10px] font-800 uppercase text-zinc-400">Height (px)</span>
                <input type="number" min="320" max="640" value={height} onChange={(e) => setHeight(e.target.value)} data-testid="india-partners-height"
                  className="mt-1 w-full text-sm border border-zinc-300 rounded-xl px-3 py-2 focus:outline-none focus:border-blue-400" />
              </label>
            </div>
            <div>
              <span className="text-[10px] font-800 uppercase text-zinc-400">Paste this on your site</span>
              <pre className="mt-1 bg-zinc-900 text-emerald-300 text-[11px] rounded-xl p-3.5 overflow-x-auto whitespace-pre-wrap break-all" data-testid="india-partners-snippet">{snippet}</pre>
              <button onClick={copy} data-testid="india-partners-copy"
                className="mt-2 inline-flex items-center gap-1.5 bg-blue-700 text-white text-xs font-800 rounded-full px-4 py-2 hover:bg-blue-800">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied!" : "Copy snippet"}
              </button>
            </div>
            <ul className="text-xs text-zinc-500 space-y-1 list-disc pl-4">
              <li>Works on any website — WordPress, Wix, plain HTML, blogs.</li>
              <li>Visitors' clicks open IndianAirports.com / TravelerPulse in a new tab; your page stays put.</li>
              <li>Every click is tagged with your domain (utm_campaign) so traffic is attributable.</li>
            </ul>
          </div>
        </div>

        <div>
          <h2 className="font-heading font-800 text-lg md:text-lg">Live preview</h2>
          <div className="mt-4">
            <iframe key={previewSrc} src={previewSrc} title="Widget preview" data-testid="india-partners-preview"
              style={{ width: "100%", maxWidth: 440, height: h }}
              className="border border-zinc-200 rounded-2xl bg-white" />
          </div>
        </div>
      </div>
    </div>
  );
}
